import { Modal, Form, Button } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { useContext } from "react";
import AgentService from "../../../services/AgentService";
import CourseService from "../../../services/CourseService";
import AlertService from "../../../services/AlertService";
import AuthContext from "../../../auth/AuthContext";
import ConfirmDialogBox from "../../confirmDialog/ConfirmDialogBox";
import { FaTrashAlt } from "react-icons/fa";

const AddAgentModal = ({ show, handleClose, isEditMode, agentData, users, reloadAgents }) => {
  const authContext = useContext(AuthContext);
  const [agentId, setAgentId] = useState(null);
  const [agentType, setAgentType] = useState(null);
  const [active, setActive] = useState(true);
  const [id, setId] = useState(0);
  const [agentIdValid, setAgentIdValid] = useState(true);
  const [agentTypeValid, setAgentTypeValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const submitControllerRef = useRef(null);


  const agentTypes = [{ value: 1, label: "Support" }];


useEffect(() => {
    const controller = new AbortController();

    const loadCourses = async () => {
      if (!show) return;
      setLoading(true);
      try {
        const res = await CourseService.GetAll(
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (res.isSuccess) {
          const courseOptions = res.data.map(course => ({
            label: `${course.courseName}`,
            value: course.id
          }));
          setCourses(courseOptions);
        } else {
          AlertService.error("Failed to load courses");
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        AlertService.error("Error loading courses");
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    loadCourses();

    return () => controller.abort();
  }, [show, authContext]);
  useEffect(() => {
    if (agentData) {
      setId(agentData.id);
      setAgentId(agentData.agentId);
      setAgentType(agentData.agentType);
      setActive(agentData.isActive);
      setSelectedCourses(agentData.courses || []);
    } else {
      resetForm();
    }
  }, [agentData, show]);

  const resetForm = () => {
    setId(0);
    setAgentId(null);
    setAgentType(null);
    setActive(true);
    setAgentIdValid(true);
    setAgentTypeValid(true);
    setSelectedCourses([]);
  };

const onSubmitHandler = async () => {
  let isValid = true;
  if (!agentId) {
    setAgentIdValid(false);
    isValid = false;
  } else setAgentIdValid(true);
  if (!agentType) {
    setAgentTypeValid(false);
    isValid = false;
  } else setAgentTypeValid(true);

  if (!isValid) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  const payload = {
    instanceId: 1,
    agentId,
    agentType,
    isActive: active,
    courses: selectedCourses,
  };
  if (id > 0) payload.id = id;

  try {
    const res = await AgentService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (res.isSuccess) {
      AlertService.success("Agent saved successfully!");
      reloadAgents();
      handleClose();
      resetForm();
    } else {
      AlertService.error(res.errorMessage || "Failed to save agent");
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("An error occurred while saving");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  const handleDeleteClick = () => {
    handleClose();
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await AgentService.Remove(id, authContext);
      if (res.isSuccess) {
        AlertService.success("Agent deleted successfully!");
        reloadAgents();
      } else {
        AlertService.error(res.errorMessage || "Failed to delete agent");
      }
    } catch (err) {
      AlertService.error("An error occurred while deleting");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="xl" backdrop="static" enforceFocus={false}>
        <Modal.Header closeButton className="border-0 pt-3">
          <Modal.Title>{isEditMode ? "Edit Agent" : "Add New Agent"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Agent Type</Form.Label>
              <Dropdown
                value={agentType}
                options={agentTypes}
                onChange={(e) => setAgentType(e.value)}
                placeholder="Select Agent Type"
                className="w-100 custom-input custom-input-color select-dropdown"
                panelClassName="select-dropdown-panel"
                appendTo="self"
              />
              {!agentTypeValid && <small className="text-danger">Please select agent type</small>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Agent</Form.Label>
              <Dropdown
                value={agentId}
                options={users}
                filter
                onChange={(e) => setAgentId(e.value)}
                placeholder="Select an Agent"
                optionLabel="label"
                optionValue="value"
                className="w-100 custom-input select-dropdown custom-input-color text-primary"
                panelClassName="select-dropdown-panel"
                emptyMessage="No user found"
                emptyFilterMessage="No matching user found"
                appendTo="self"
              />
              {!agentIdValid && <small className="text-danger">Please select agent</small>}
            </Form.Group>

            {/* MultiSelect for Courses */}
            <Form.Group className="mb-2">
              <Form.Label>Select Courses</Form.Label>
              <MultiSelect
                value={selectedCourses}
                options={courses}
                onChange={(e) => setSelectedCourses(e.value)}
                placeholder="Select Courses"
                filter
                display="chip"
                className="w-100 custom-input-color"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4">
          <div className="d-flex justify-content-between align-items-center w-100">

            <div className="d-flex gap-3">
              {isEditMode && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleDeleteClick}
                  className="py-2 px-3"
                >
                  <FaTrashAlt className="mb-1 me-1" /> Delete
                </Button>
              )}
              <Form.Check
                type="checkbox"
                id="activeCheckbox"
                label="Active"
                checked={active}
                className="custom-checkbox d-flex gap-2 align-items-center text-success"
                onChange={(e) => setActive(e.target.checked)}
              />

            </div>
            <div className="d-flex gap-3">
              <Button variant="outline-secondary" className="py-2 px-5" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <button className="py-2 px-5 btn btn-primary" onClick={onSubmitHandler} disabled={loading}>
                Submit
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          setShowDeleteDialog(false);
          await handleDelete();
        }}
        title={
          <>
            Are you sure you want to delete this agent?
          </>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default AddAgentModal;